import { motion } from "framer-motion";
import { Check, Minus, ArrowUpRight, ShieldCheck, Scale, Fingerprint, Briefcase, Quote } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { LS, LS_ABOUT, LS_PLANS, LS_COMPARISON, LS_MEMBERSHIPS, LS_SUPPLEMENTS, LS_MEMBER_QUOTE } from "@/data";

const PLAN_ICONS = { personal: Scale, idshield: Fingerprint, business: Briefcase };

const LSButton = ({ children, testid, className = "" }) => (
  <a
    href={LS.signup}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`btn-sweep group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(130,49,212,0.7)] transition-transform duration-300 hover:scale-[1.03] ${className}`}
    style={{ backgroundColor: LS.purple }}
  >
    {children}
    <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

export default function LegalShieldPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div
          className="absolute -top-1/4 left-1/3 -translate-x-1/2 h-[60vh] w-[60vh] rounded-full blur-[150px]"
          style={{ backgroundColor: "rgba(130,49,212,0.22)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src={LS.logo}
              alt="LegalShield"
              className="h-16 md:h-20 w-auto object-contain"
              data-testid="ls-hero-logo"
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.9 }}
              className="mt-8 font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#b98bff]"
            >
              Heritage Family Protection Agency · Independent Associate
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-serif-display text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight text-white leading-[0.95]"
            >
              A law firm <span className="italic" style={{ color: "#b98bff" }}>in your corner.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="mt-6 max-w-xl font-body font-light text-base md:text-lg leading-relaxed text-white/65"
            >
              Affordable legal protection for your family and business — powered by LegalShield. Talk to real
              attorneys, protect your identity, and handle life's legal moments with confidence, all for one low
              monthly rate.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.9 }}
              className="mt-9"
            >
              <LSButton testid="ls-signup-hero">Get Lawyer Access Now</LSButton>
            </motion.div>
          </div>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl" style={{ border: "1px solid rgba(130,49,212,0.35)" }} />
              <div className="relative overflow-hidden rounded-3xl">
                <img src={LS.images.familyLawyer} alt="A family meeting with their attorney" className="h-[420px] md:h-[520px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT / BIO */}
      <section className="relative py-20 md:py-28 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <div className="relative pb-16 pr-16">
              <div className="relative overflow-hidden rounded-2xl" style={{ border: "1px solid rgba(130,49,212,0.3)" }}>
                <img src={LS.images.consult} alt="Attorney consultation" className="h-[360px] md:h-[440px] w-full object-cover" />
              </div>
              <div className="absolute bottom-0 right-0 w-2/3 overflow-hidden rounded-2xl border-2 border-[#0a192f] shadow-[0_20px_50px_rgba(0,0,0,0.6)]" style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}>
                <img src={LS.images.signing} alt="Signing legal documents" className="h-[200px] md:h-[240px] w-full object-cover" />
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#b98bff" }}>Why LegalShield</p>
              <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white">
                Justice for all, <span className="italic" style={{ color: "#b98bff" }}>not just a few.</span>
              </h2>
            </Reveal>
            {LS_ABOUT.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.06}>
                <p className={`mt-5 font-body font-light leading-relaxed ${i === 0 ? "text-white/75 text-lg" : "text-white/55 text-base md:text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="mt-8">
                <LSButton testid="ls-signup-about">Get Lawyer Access Now</LSButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS & PRICING */}
      <section className="relative py-24 md:py-32 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#b98bff" }}>Memberships & Pricing</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Smart, simple — <span className="italic" style={{ color: "#b98bff" }}>coverage starts here.</span>
            </h2>
            <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg text-white/60">
              Protection for your family, your business, and your personal identity.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LS_MEMBERSHIPS.map((m, i) => (
              <Reveal key={m.name} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`ls-membership-${i}`}
                  className="relative h-full flex flex-col rounded-3xl border bg-black/40 p-8 transition-colors duration-500"
                  style={{ borderColor: m.popular ? "rgba(130,49,212,0.6)" : "rgba(255,255,255,0.1)" }}
                >
                  {m.popular && (
                    <span className="absolute top-6 right-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white" style={{ backgroundColor: LS.purple }}>
                      Most Popular
                    </span>
                  )}
                  <h3 className="font-serif-display text-2xl md:text-3xl text-white pr-20">{m.name}</h3>
                  <p className="mt-3">
                    <span className="font-serif-display text-4xl" style={{ color: "#b98bff" }}>{m.price}</span>
                    <span className="font-body text-sm text-white/45">{m.per}</span>
                  </p>
                  {m.note && <p className="mt-1 font-body text-xs text-white/40">{m.note}</p>}
                  <p className="mt-4 font-body font-light text-sm leading-relaxed text-white/60">{m.blurb}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {m.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/65">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: "#b98bff" }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={m.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ls-membership-cta-${i}`}
                    className="mt-7 inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
                    style={{ backgroundColor: LS.purple }}
                  >
                    Get Started <ArrowUpRight size={15} />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12">
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/40">Optional Legal Plan Supplements</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {LS_SUPPLEMENTS.map((s) => (
                  <a
                    key={s.name}
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full border border-white/15 px-5 py-2.5 font-body text-sm text-white/65 hover:text-white transition-colors duration-300"
                    style={{ borderColor: "rgba(185,139,255,0.3)" }}
                  >
                    {s.name}
                  </a>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <figure className="mt-14 rounded-3xl border border-white/10 bg-black/30 p-8 md:p-12 max-w-3xl">
              <Quote size={28} style={{ color: "rgba(185,139,255,0.5)" }} strokeWidth={1.2} />
              <blockquote className="mt-4 font-serif-display text-lg md:text-xl italic leading-relaxed text-white/80">
                "{LS_MEMBER_QUOTE.quote}"
              </blockquote>
              <figcaption className="mt-5 font-body text-sm text-white/50">
                — {LS_MEMBER_QUOTE.name}, {LS_MEMBER_QUOTE.context}
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* PLANS */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#b98bff" }}>Membership Plans</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Choose the protection <span className="italic" style={{ color: "#b98bff" }}>that fits your life.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {LS_PLANS.map((plan, i) => {
              const Icon = PLAN_ICONS[plan.id];
              return (
                <Reveal key={plan.id} delay={i * 0.08}>
                  <div
                    data-testid={`ls-plan-${plan.id}`}
                    className="group relative h-full flex flex-col overflow-hidden rounded-3xl border bg-[#0a192f] p-8 transition-colors duration-500"
                    style={{ borderColor: plan.highlight ? "rgba(130,49,212,0.6)" : "rgba(255,255,255,0.1)" }}
                  >
                    {plan.highlight && (
                      <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full blur-[80px]" style={{ backgroundColor: "rgba(130,49,212,0.25)" }} />
                    )}
                    {plan.highlight && (
                      <span className="absolute top-6 right-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white" style={{ backgroundColor: LS.purple }}>
                        Most Popular
                      </span>
                    )}
                    <div className="relative">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ border: "1px solid rgba(130,49,212,0.4)", color: "#b98bff" }}>
                        <Icon size={22} strokeWidth={1.5} />
                      </span>
                      <h3 className="mt-6 font-serif-display text-3xl text-white">{plan.name}</h3>
                      <p className="mt-1 font-body text-sm" style={{ color: "#b98bff" }}>{plan.tagline}</p>

                      <ul className="mt-6 space-y-3">
                        {plan.features.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/65">
                            <Check size={16} className="mt-0.5 shrink-0" style={{ color: "#b98bff" }} />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="relative mt-8 pt-2">
                      <LSButton testid={`ls-signup-${plan.id}`} className="w-full">Get Lawyer Access Now</LSButton>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal>
            <p className="mt-8 font-body text-xs text-white/35 max-w-3xl leading-relaxed">
              Plan availability, features, and pricing may vary by state and selected membership. LegalShield is a
              product of Pre-Paid Legal Services, Inc. Heritage Family Protection Agency is an Independent Associate.
              This page is for general information and is not legal advice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="relative py-20 md:py-28 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: "#b98bff" }}>Compare Plans</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Side by side, <span className="italic" style={{ color: "#b98bff" }}>at a glance.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-12 overflow-x-auto rounded-3xl border border-white/10" data-testid="ls-comparison-table">
              <table className="w-full min-w-[640px] border-collapse font-body text-sm">
                <thead>
                  <tr className="bg-black/50">
                    <th className="text-left px-6 py-5 font-body text-xs tracking-[0.2em] uppercase text-white/50">Feature</th>
                    {LS_COMPARISON.columns.map((c) => (
                      <th key={c} className="px-6 py-5 text-center">
                        <span className="font-serif-display text-lg text-white">{LS_COMPARISON.labels[c]}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {LS_COMPARISON.rows.map((row, i) => (
                    <tr key={row.feature} className={`border-t border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`} data-testid={`ls-compare-row-${i}`}>
                      <td className="px-6 py-4 text-white/70">{row.feature}</td>
                      {LS_COMPARISON.columns.map((c) => (
                        <td key={c} className="px-6 py-4 text-center">
                          {row[c] ? (
                            <Check size={18} className="inline" style={{ color: "#b98bff" }} />
                          ) : (
                            <Minus size={16} className="inline text-white/15" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t border-white/10 bg-black/40">
                    <td className="px-6 py-6" />
                    {LS_COMPARISON.columns.map((c) => (
                      <td key={c} className="px-6 py-6 text-center">
                        <a
                          href={LS.signup}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`ls-compare-signup-${c}`}
                          className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 font-body text-xs font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-105"
                          style={{ backgroundColor: LS.purple }}
                        >
                          Choose Plan <ArrowUpRight size={13} />
                        </a>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: "linear-gradient(180deg, rgba(130,49,212,0.14) 0%, #081226 100%)" }}>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <ShieldCheck size={44} className="mx-auto" style={{ color: "#b98bff" }} strokeWidth={1.5} />
            <h2 className="mt-6 font-serif-display text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight text-white">
              Get the protection <span className="italic" style={{ color: "#b98bff" }}>you deserve.</span>
            </h2>
            <p className="mt-6 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
              Sign up in minutes and gain instant access to a dedicated provider law firm. Peace of mind starts today.
            </p>
            <div className="mt-10 flex justify-center">
              <LSButton testid="ls-signup-cta">Get Lawyer Access Now</LSButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
